import { useEffect, useState, useCallback } from "react";
import { FiChevronLeft, FiChevronRight, FiX } from "react-icons/fi";
import { createPortal } from "react-dom";

const ProductImageGallery = ({ images = [] }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isFullScreen, setIsFullScreen] = useState(false);

  const count = images?.length || 0;
  const imagesKey = (images || []).join("|");

  // Reset to first image whenever the variant (and so the image set) changes
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [imagesKey]);

  const nextImage = useCallback(
    () => setCurrentImageIndex((prev) => (prev < count - 1 ? prev + 1 : 0)),
    [count]
  );
  const prevImage = useCallback(
    () => setCurrentImageIndex((prev) => (prev > 0 ? prev - 1 : count - 1)),
    [count]
  );

  // Lock page scroll + keyboard controls while fullscreen is open
  useEffect(() => {
    if (!isFullScreen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") setIsFullScreen(false);
      if (e.key === "ArrowRight") nextImage();
      if (e.key === "ArrowLeft") prevImage();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [isFullScreen, nextImage, prevImage]);

  const Thumbs = ({ dark = false }) => (
    <>
      {images.map((image, index) => (
        <button
          key={index}
          type="button"
          onClick={() => setCurrentImageIndex(index)}
          className={`shrink-0 w-14 h-14 md:w-16 md:h-16 rounded-md overflow-hidden bg-white border-2 transition-colors ${currentImageIndex === index
              ? "border-yellow-800"
              : dark
                ? "border-transparent opacity-70 hover:opacity-100"
                : "border-gray-200 hover:border-yellow-800/50"
            }`}
        >
          <img
            src={image}
            alt={`Thumbnail ${index + 1}`}
            className="w-full h-full object-contain p-0.5"
          />
        </button>
      ))}
    </>
  );

  return (
    <div className="flex flex-col-reverse md:flex-row gap-3">
      {/* Thumbnails: column on desktop, row on mobile */}
      {count > 1 && (
        <div className="flex md:flex-col gap-2 overflow-x-auto md:overflow-y-auto md:max-h-[480px] md:w-16 shrink-0 pb-1 md:pb-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <Thumbs />
        </div>
      )}

      {/* Main image */}
      <div className="relative flex-1 aspect-square bg-white border border-gray-200 rounded-lg overflow-hidden">
        {count > 0 ? (
          <img
            src={images[currentImageIndex]}
            alt="Product"
            onClick={() => setIsFullScreen(true)}
            className="w-full h-full object-contain p-3 cursor-zoom-in"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-100 text-sm text-gray-500">
            No image available
          </div>
        )}

        {count > 1 && (
          <>
            <button
              type="button"
              aria-label="Previous image"
              onClick={prevImage}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 rounded-full p-1.5 shadow hover:bg-white"
            >
              <FiChevronLeft size={18} />
            </button>
            <button
              type="button"
              aria-label="Next image"
              onClick={nextImage}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 rounded-full p-1.5 shadow hover:bg-white"
            >
              <FiChevronRight size={18} />
            </button>
            <span className="absolute bottom-2 right-2 text-[11px] bg-black/60 text-white rounded-full px-2 py-0.5">
              {currentImageIndex + 1} / {count}
            </span>
          </>
        )}
      </div>

      {/* Fullscreen viewer */}
      {isFullScreen &&
        createPortal(
          <div className="fixed inset-0 z-[99999] bg-black/90 flex flex-col items-center justify-center p-4">
            <button
              type="button"
              aria-label="Close"
              onClick={() => setIsFullScreen(false)}
              className="absolute top-4 right-4 text-white p-2 rounded-full hover:bg-white/10"
            >
              <FiX size={24} />
            </button>

            <div className="w-full max-w-4xl h-[75vh] flex items-center justify-center">
              <img
                src={images[currentImageIndex]}
                alt="Product"
                className="max-w-full max-h-full object-contain"
              />
            </div>

            {count > 1 && (
              <>
                <button
                  type="button"
                  aria-label="Previous image"
                  onClick={prevImage}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 rounded-full p-3 hover:bg-white"
                >
                  <FiChevronLeft size={22} />
                </button>
                <button
                  type="button"
                  aria-label="Next image"
                  onClick={nextImage}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 rounded-full p-3 hover:bg-white"
                >
                  <FiChevronRight size={22} />
                </button>
                <div className="flex mt-4 gap-2 overflow-x-auto max-w-full pb-2">
                  <Thumbs dark />
                </div>
              </>
            )}
          </div>,
          document.body
        )}
    </div>
  );
};

export default ProductImageGallery;